import { Building2, FolderKanban, Moon, Plus, Rows2, Rows3, Sun } from 'lucide-react'
import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { useCompanies } from '@/features/companies/api'
import { usePortfolioProjects } from '@/features/portfolio/api'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/components/ui/Command'
import { useCommandPalette } from '@/shared/stores/command-palette-store'
import { useUiStore } from '@/shared/stores/ui-store'

import { CompanyMonogram } from './CompanyMonogram'
import { useT } from '@/shared/i18n'

/**
 * El conmutador `⌘K`.
 *
 * Es la navegación real de un equipo que vive en la app: con catorce proyectos
 * de cinco empresas, llegar a uno cuesta dos clicks y una lectura; acá cuesta
 * tres letras. Salta a una empresa o a un proyecto, arranca una tarea y cambia
 * tema y densidad.
 *
 * **No se monta en el portal.** No es que esté escondido: el componente se
 * monta en `InternalLayout` y el árbol del portal usa otro layout, así que en
 * `/portal` no existe el listener de teclado ni el diálogo. Un cliente no puede
 * abrir un buscador que lista empresas.
 *
 * Los datos salen de las queries que ya tiene la app, con su cache: abrir el
 * conmutador no dispara requests nuevos si ya estuviste en el portafolio.
 */
export function CommandPalette() {
  const t = useT()
  const open = useCommandPalette((state) => state.open)
  const setOpen = useCommandPalette((state) => state.setOpen)
  const toggle = useCommandPalette((state) => state.toggle)
  const navigate = useNavigate()

  const companies = useCompanies()
  // `⌘K` es para saltar a cualquier proyecto, también a uno ya entregado.
  const projects = usePortfolioProjects({}, { needsDone: true })

  const theme = useUiStore((state) => state.theme)
  const density = useUiStore((state) => state.density)
  const toggleTheme = useUiStore((state) => state.toggleTheme)
  const toggleDensity = useUiStore((state) => state.toggleDensity)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return
      event.preventDefault()
      toggle()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [toggle])

  const run = useCallback(
    (action: () => void) => {
      action()
      setOpen(false)
    },
    [setOpen],
  )

  const go = useCallback(
    (path: string) => {
      run(() => void navigate(path))
    },
    [navigate, run],
  )

  const themeNext = t(theme === 'dark' ? 'appearance.theme.nameLight' : 'appearance.theme.nameDark')
  const densityNext = t(
    density === 'compact' ? 'appearance.density.nameComfortable' : 'appearance.density.nameCompact',
  )

  return (
    <CommandDialog open={open} onClose={() => setOpen(false)} label={t('command.label')}>
      <CommandInput placeholder={t('command.placeholder')} autoFocus />

      <CommandList>
        <CommandEmpty>{t('command.empty')}</CommandEmpty>

        <CommandGroup heading={t('command.group.companies')}>
          {(companies.data ?? []).map((company) => (
            <CommandItem
              key={company.id}
              value={`${company.name} ${company.slug} ${t('common.label.company')}`}
              onSelect={() => go(`/companies/${company.id}`)}
            >
              <CompanyMonogram name={company.name} hue={company.color_hue} />
              {company.name}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandGroup heading={t('command.group.projects')}>
          {(projects.data ?? []).map((project) => (
            <CommandItem
              key={project.id}
              value={`${project.name} ${project.company.name} ${t('common.label.project')}`}
              onSelect={() => go(`/projects/${project.id}`)}
            >
              <FolderKanban size={14} aria-hidden="true" className="text-text-3" />
              <span className="flex-1 truncate">{project.name}</span>
              <span className="text-2xs text-text-3">{project.company.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        {/*
          Crear una tarea necesita saber en qué proyecto: el conmutador lleva
          hasta el alta con el foco puesto, en vez de pedir un título a ciegas y
          después preguntar dónde va.
        */}
        <CommandGroup heading={t('command.group.newTaskIn')}>
          {(projects.data ?? []).map((project) => (
            <CommandItem
              key={`nueva-${project.id}`}
              value={`${t('command.newTaskIn', { proyecto: project.name })} ${project.company.name}`}
              onSelect={() => go(`/projects/${project.id}/tareas?nueva=1`)}
            >
              <Plus size={14} aria-hidden="true" className="text-text-3" />
              <span className="flex-1 truncate">
                {t('command.newTaskIn', { proyecto: project.name })}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandGroup heading={t('command.group.appearance')}>
          <CommandItem
            value={t('appearance.theme.switchTo', { modo: themeNext })}
            onSelect={() => run(toggleTheme)}
          >
            {theme === 'dark' ? (
              <Sun size={14} aria-hidden="true" className="text-text-3" />
            ) : (
              <Moon size={14} aria-hidden="true" className="text-text-3" />
            )}
            {t('appearance.theme.switchTo', { modo: themeNext })}
          </CommandItem>

          <CommandItem
            value={t('appearance.density.switchTo', { densidad: densityNext })}
            onSelect={() => run(toggleDensity)}
          >
            {density === 'compact' ? (
              <Rows2 size={14} aria-hidden="true" className="text-text-3" />
            ) : (
              <Rows3 size={14} aria-hidden="true" className="text-text-3" />
            )}
            {t('appearance.density.switchTo', { densidad: densityNext })}
          </CommandItem>

          <CommandItem value={t('command.seeAllCompanies')} onSelect={() => go('/companies')}>
            <Building2 size={14} aria-hidden="true" className="text-text-3" />
            {t('command.seeAllCompanies')}
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}
